/**
 * FolderBreadcrumbs
 *
 * Displays the path from root to the currently selected folder.
 * Each segment is clickable; clicking navigates to that folder.
 * "All slides" is always the first segment (root).
 */

import React, { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { SlideSenseFolder } from "@/features/slide-sense/types"

import { ChevronRight, LayoutGrid } from "@/components/icons"
import { cn } from "@/lib/utils"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface FolderBreadcrumbsProps {
  folders: SlideSenseFolder[]
  selectedFolderId: string | null
  onSelectFolder: (id: string | null) => void
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Walk the parent_folder_id chain from selected folder to root, then reverse. */
function buildPath(folders: SlideSenseFolder[], selectedId: string | null): SlideSenseFolder[] {
  if (!selectedId) return []

  const byId = new Map(folders.map((f) => [f.id, f]))
  const path: SlideSenseFolder[] = []
  let current = byId.get(selectedId)

  // Guard against circular references — max 20 iterations
  let safety = 20
  while (current && safety > 0) {
    path.push(current)
    if (current.parent_folder_id) {
      current = byId.get(current.parent_folder_id)
    } else {
      break
    }
    safety--
  }

  return path.reverse()
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FolderBreadcrumbs({
  folders,
  selectedFolderId,
  onSelectFolder,
}: FolderBreadcrumbsProps) {
  const { t } = useTranslation()
  const path = useMemo(() => buildPath(folders, selectedFolderId), [folders, selectedFolderId])

  return (
    <nav
      aria-label={t("slideSense.folderBreadcrumbs")}
      className="text-muted-foreground flex items-center gap-1 text-xs"
    >
      {/* Root: "All slides" */}
      <button
        type="button"
        className={cn(
          "hover:bg-accent hover:text-accent-foreground inline-flex items-center gap-1 rounded px-1.5 py-0.5 transition-colors",
          selectedFolderId === null && "text-foreground font-medium"
        )}
        onClick={() => onSelectFolder(null)}
      >
        <LayoutGrid className="h-3 w-3 shrink-0" />
        {t("slideSense.allSlides")}
      </button>

      {/* Segments */}
      {path.map((folder, index) => {
        const isLast = index === path.length - 1
        return (
          <React.Fragment key={folder.id}>
            <ChevronRight className="text-muted-foreground/50 h-3 w-3 shrink-0" />
            <button
              type="button"
              className={cn(
                "hover:bg-accent hover:text-accent-foreground inline-flex items-center rounded px-1.5 py-0.5 transition-colors",
                isLast && "text-foreground font-medium"
              )}
              onClick={() => onSelectFolder(folder.id)}
            >
              <span className="max-w-[160px] truncate">{folder.name}</span>
            </button>
          </React.Fragment>
        )
      })}
    </nav>
  )
}
